/**
 * OfflineIndicator - Zeigt Offline-Status und ausstehende Uploads
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Wifi, WifiOff, Cloud, CloudOff, RefreshCw, Trash2, X, Check } from 'lucide-react';
import { 
  getAllPendingUploads, 
  deleteUpload, 
  countPendingUploads,
  createPreviewUrl,
  revokePreviewUrl
} from '../utils/offlineStorage';
import { syncPendingUploads, retryUpload, addSyncListener } from '../utils/offlineSync';

const OfflineIndicator = ({ onClose, currentUser }) => {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [showQueue, setShowQueue] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({});

  // Online/Offline Status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Pending Uploads laden
  useEffect(() => {
    const loadPending = async () => {
      const count = await countPendingUploads();
      setPendingCount(count);
      
      if (showQueue) {
        const allUploads = await getAllPendingUploads();
        setUploads(allUploads);
        
        // Preview URLs erstellen
        const urls = {};
        allUploads.forEach(upload => {
          if (upload.blob && upload.type !== 'voice') {
            urls[upload.id] = createPreviewUrl(upload.blob);
          }
        });
        setPreviewUrls(urls);
      }
    };
    
    loadPending();
    const interval = setInterval(loadPending, 5000);
    
    return () => {
      clearInterval(interval);
      // Preview URLs freigeben
      Object.values(previewUrls).forEach(url => revokePreviewUrl(url));
    };
  }, [showQueue]);

  // Sync-Events abonnieren
  useEffect(() => {
    const unsubscribe = addSyncListener((event, data) => {
      if (event === 'sync-start') setSyncing(true);
      if (event === 'sync-complete') {
        setSyncing(false);
        loadUploads();
      }
      if (event === 'upload-complete' || event === 'upload-failed') {
        loadUploads();
      }
    });
    
    return unsubscribe;
  }, []);

  const loadUploads = async () => {
    const count = await countPendingUploads();
    setPendingCount(count);
    if (showQueue) {
      const allUploads = await getAllPendingUploads();
      setUploads(allUploads);
    }
  };

  const handleSync = async () => {
    if (!isOnline) {
      alert('Keine Internetverbindung');
      return;
    }
    setSyncing(true);
    try {
      const result = await syncPendingUploads();
      if (result.synced > 0) {
        alert(t('offline.syncSuccess', '{{count}} upload(s) successfully synchronized!').replace('{{count}}', result.synced));
      }
      if (result.failed > 0) {
        alert(t('offline.syncFailed', '{{count}} upload(s) failed. Please try again.').replace('{{count}}', result.failed));
      }
    } catch (error) {
      alert(t('offline.syncError', 'Error synchronizing: ') + error.message);
    } finally {
      setSyncing(false);
      loadUploads();
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryUpload(id);
      loadUploads();
    } catch (error) {
      alert('Erneuter Versuch fehlgeschlagen: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Upload wirklich löschen? Die Daten gehen verloren.')) {
      await deleteUpload(id);
      loadUploads();
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return '📷';
      case 'voice': return '🎤';
      case 'defect': return '⚠️';
      default: return '📁';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'uploading': return 'bg-blue-500 animate-pulse';
      case 'failed': return 'bg-red-500';
      case 'completed': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  // Kompakte Ansicht wenn keine ausstehenden Uploads
  if (pendingCount === 0 && isOnline) {
    return null;
  }

  return (
    <>
      {/* Floating Indicator */}
      <button
        onClick={() => setShowQueue(true)}
        className={`fixed bottom-24 right-4 z-40 p-3 rounded-full shadow-lg flex items-center gap-2
                   ${isOnline ? 'bg-emerald-600' : 'bg-red-600'} text-white`}
      >
        {isOnline ? <Wifi size={20} /> : <WifiOff size={20} />}
        {pendingCount > 0 && (
          <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
            {pendingCount}
          </span>
        )}
      </button>

      {/* Queue Modal */}
      {showQueue && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-gray-900 w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isOnline ? (
                  <div className="flex items-center gap-2 text-emerald-400">
                    <Cloud size={24} />
                    <span className="font-bold">Online</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-400">
                    <CloudOff size={24} />
                    <span className="font-bold">Offline</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowQueue(false)}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {pendingCount === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Check size={48} className="mx-auto mb-3 text-emerald-400" />
                  <p>Alle Uploads synchronisiert!</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-gray-300">
                      {pendingCount} ausstehende Upload(s)
                    </span>
                    <button
                      onClick={handleSync}
                      disabled={!isOnline || syncing}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw size={18} className={syncing ? 'animate-spin' : ''} />
                      {syncing ? 'Syncing...' : 'Jetzt synchronisieren'}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {uploads.map(upload => (
                      <div 
                        key={upload.id}
                        className="bg-gray-800 rounded-xl p-3 flex items-center gap-3"
                      >
                        {/* Preview */}
                        <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                          {previewUrls[upload.id] ? (
                            <img 
                              src={previewUrls[upload.id]} 
                              alt="Preview" 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl">{getTypeIcon(upload.type)}</span>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-2 h-2 rounded-full ${getStatusColor(upload.status)}`} />
                            <span className="text-white font-medium capitalize">
                              {upload.type === 'defect' ? t('baustelle.defect', 'Defect') : upload.type === 'photo' ? t('files.photo', 'Photo') : t('chat.voiceMessage', 'Voice')}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm truncate">
                            {upload.description || upload.caption || t('common.noDescription', 'No description')}
                          </p>
                          <p className="text-gray-500 text-xs">
                            {new Date(upload.createdAt).toLocaleString(currentUser?.language || 'de')}
                          </p>
                          {upload.status === 'failed' && upload.lastError && (
                            <p className="text-red-400 text-xs mt-1">
                              {t('common.error', 'Error')}: {upload.lastError}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 shrink-0">
                          {upload.status === 'failed' && isOnline && (
                            <button
                              onClick={() => handleRetry(upload.id)}
                              className="p-2 bg-blue-600 text-white rounded-lg"
                              title={t('common.retry', 'Erneut versuchen')}
                            >
                              <RefreshCw size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(upload.id)}
                            className="p-2 bg-red-600 text-white rounded-lg"
                            title={t('common.delete', 'Löschen')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Footer Info */}
            {!isOnline && (
              <div className="p-4 bg-yellow-900/50 border-t border-yellow-700">
                <p className="text-yellow-200 text-sm text-center">
                  📴 Sie sind offline. Uploads werden automatisch synchronisiert, sobald Sie wieder online sind.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OfflineIndicator;
